

























































































































































import {
  defineComponent,
  ref,
  useContext,
  computed,
  onMounted,
  onUnmounted,
  useRoute,
  useRouter,
  useFetch,
  useMeta,
} from '@nuxtjs/composition-api';
import { castArray } from 'lodash-es';
import { ValidationObserver } from 'vee-validate';
import LoginWithAmazon from '~/components/pages/Login/LoginWithAmazon.vue';
import LoginWithApple from '~/components/pages/Login/LoginWithApple.vue';
import LoginWithRakuten from '~/components/pages/Login/LoginWithRakuten.vue';
import AppCancelButton from '~/components/partials/App/AppCancelButton.vue';
import AppIcon from '~/components/partials/App/AppIcon.vue';
import AppLink from '~/components/partials/App/AppLink.vue';
import AppSubmitButton from '~/components/partials/App/AppSubmitButton.vue';
import FormField from '~/components/partials/Form/FormField.vue';
import FormInput from '~/components/partials/Form/FormInput.vue';
import LayoutDefault from '~/components/partials/Layout/LayoutDefault.vue';
import { useAuthentication } from '~/composables/auth/useAuthentication';
import { useDECConnect } from '~/composables/auth/useDECConnect';
import { useSocialLogin } from '~/composables/auth/useSocialLogin';
import { useEnotecaApp } from '~/composables/useEnotecaApp';
import { useLayoutProps } from '~/composables/useLayoutProps';
import { useStore } from '~/composables/useStore';
import { COOKIE_KEY, FIXED_VIEWPORT } from '~/constants';
import { url } from '~/utils';
import { sendLoginEvent as sendEvent } from '~/utils/ga';
import { SocialLoginProvider } from '~/enums';
import { LoginStatus, LoginCategory } from '~/enums/login';

export default defineComponent({
  components: {
    LayoutDefault,
    FormField,
    FormInput,
    AppIcon,
    AppLink,
    AppSubmitButton,
    AppCancelButton,
    LoginWithAmazon,
    LoginWithRakuten,
    LoginWithApple,
  },
  setup(props, { root: { $nuxt } }) {
    const { $axios, $cookies, redirect } = useContext();
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const { getLoginUrl } = useSocialLogin();
    const loginId = ref('');
    const password = ref('');
    const keepLogin = ref(true);
    const { postLogin, isAuthenticated } = useAuthentication();
    const { isEnotecaAppWebView } = useEnotecaApp();
    const { dconId } = useDECConnect();
    const isProgress = ref(false);
    const isErrorMessageVisible = ref(false);
    const observerRef = ref<InstanceType<typeof ValidationObserver>>();

    const clickSignup = () => {
      if (dconId.value != null) {
        try {
          redirect(getLoginUrl(SocialLoginProvider.LINE, url('SIGN_UP')));
        } catch {
          // 何もしない
        }
        return;
      }
      router.push(url('SIGN_UP'));
    };
    const returnTo = computed(() => $cookies.get(COOKIE_KEY.RETURN_TO) || url('TOP'));

    // クエリがあればクエリを除いた canonical を返す
    const canonical = computed(() => {
      const { path, query } = route.value;

      if (Object.keys(query).length > 0) {
        return path;
      }
    });

    const login = async (): Promise<void> => {
      if (isProgress.value) {
        return;
      }

      sendEvent(LoginStatus.TRIED, LoginCategory.NORMAL);

      try {
        isProgress.value = true;
        isErrorMessageVisible.value = false;
        const userCertification = await $axios.$post('/_api/v1/login', {
          loginId: loginId.value,
          password: password.value,
          keepLogin: keepLogin.value,
        });

        // ブログ画面のショップ登録ボタンから遷移した場合、ログインを決定したら cookie に保持する
        // （ログインしたらストアの状態がリセットされるから）
        const salesLocationId = store.state.modules.mypage.myshop.salesLocation;
        if (salesLocationId) {
          $cookies.set(COOKIE_KEY.MYSHOP_ID, salesLocationId, { path: '/' });
        }

        await postLogin(returnTo.value, userCertification);
        sendEvent(LoginStatus.SUCCEED, LoginCategory.NORMAL);
      } catch {
        isProgress.value = false;
        isErrorMessageVisible.value = true;
        password.value = '';
        observerRef.value?.reset();
      }
    };

    useFetch(() => {
      // ENOTECA アプリの場合はログイン状態に関わらず表示
      if (isEnotecaAppWebView.value) {
        return;
      }

      // ログイン状態の場合はマイページへ飛ばす
      if (isAuthenticated.value) {
        redirect(url('MYPAGE'));
      }
    });

    onMounted(async () => {
      const returnUrl = castArray(route.value.query.returnUrl)[0];

      if (returnUrl) {
        $cookies.set(COOKIE_KEY.RETURN_TO, returnUrl, { path: '/' });

        // クエリを除去
        await router.replace({ query: { returnUrl: undefined } });

        return;
      }

      // returnTo が設定されていなければ遷移元に戻る
      if (!$cookies.get(COOKIE_KEY.RETURN_TO) && $nuxt.context.from?.fullPath) {
        $cookies.set(COOKIE_KEY.RETURN_TO, $nuxt.context.from.fullPath, { path: '/' });
      }
    });

    onUnmounted(() => {
      // （ログインせず別画面に遷移したら、ショップIDをリセットする
      if (store.state.modules.mypage.myshop.salesLocation) store.commit('modules/mypage/myshop/setSalesLocation', '');
    });

    useLayoutProps(() => ({
      layout: {
        header: 'always-simple',
        headerMenu: 'never',
        footer: 'always',
        footerMenu: 'never',
        topBanner: 'never',
        helloBar: 'never',
      },
      head: {
        title: 'ログイン',
        canonical: canonical.value,
      },
    }));

    useMeta(() => {
      return {
        meta: [FIXED_VIEWPORT],
      };
    });
    return {
      url,
      loginId,
      password,
      keepLogin,
      isProgress,
      isErrorMessageVisible,
      clickSignup,
      login,
      observerRef,
      isEnotecaAppWebView,
    };
  },
  head: {},
});
